import { HttpClient, HttpResponse } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, mergeMap } from 'rxjs/operators';
import { GridQueryUtils } from '../../utility/grid-query-utils';
import { TranslateService } from '@ngx-translate/core';
import { MessageHandlerService } from '../../utility/message-handler.service';

export interface GridDataResultCustom extends GridDataResult
{
    export: any;
    data: any[];
    total: number;
}

export class GridDataService extends BehaviorSubject<GridDataResult>
{
    public loading: boolean;
    public translateService: TranslateService;

    private queryRoute: string;
    private messageHandler: MessageHandlerService;

    constructor(
        private http: HttpClient, route: string,
        private translate: TranslateService,
        private handlerService: MessageHandlerService)
    {
        super(null);
        this.queryRoute = route;
        this.messageHandler = this.handlerService;
        this.translateService = translate;
    }

    public get messageHandlerService(): MessageHandlerService
    {
        return this.messageHandler;
    }

    public get route(): string
    {
        return this.queryRoute;
    }

    public set route(newRoute)
    {
        this.queryRoute = newRoute;
    }

    public query(
        queryCallback: any = null, additionalQueryString = '',
        errorCallback: any = null, finallyCallback: any = null,
        httpMethod: 'get' | 'post' = 'get',     // Provide option to either get or post
        httpPostBody = null): void              // Provide the post body
    {
        this.queryByState(null, queryCallback, additionalQueryString, errorCallback, finallyCallback, httpMethod, httpPostBody);
    }

    public queryByState(
        state: any,
        queryCallback: any = null, additionalQueryString = '',
        errorCallback: any = null, finallyCallback: any = null,
        httpMethod: 'get' | 'post' = 'get',     // Provide option to either get or post
        httpPostBody = null,                    // Provide the post body
        additionalBaseUrlString = ''): void     // Provide additional base url value
    {
        let baseUrl = this.queryRoute;
        // If additionalQueryString is starting with /, assuming it is in addition to base url
        if (additionalBaseUrlString !== '')
        {
            baseUrl += additionalBaseUrlString;
        }
        const queryStr = state !== null ? GridQueryUtils.generateQueryString(state, baseUrl) : baseUrl;
        this[httpMethod](`${queryStr}${additionalQueryString}`, httpPostBody).subscribe({
            next: (x) =>
            {
                super.next(x);
                if (queryCallback !== null)
                {
                    queryCallback(x);
                }
            },
            error: (errorResponse) =>
            {
                this.loading = false;
                // If callback is set, it must handle error
                if (errorCallback !== null)
                {
                    errorCallback(errorResponse);
                }
                else
                {
                    this.messageHandlerService.displayError(errorResponse);
                }
            },
            complete: () =>
            {
                this.loading = false;
                if (finallyCallback !== null)
                {
                    finallyCallback();
                }
            }
        });
    }

    protected get(queryStr: string): Observable<GridDataResult>
    {
        this.loading = true;
        return this.http.get<GridDataResult>(queryStr).pipe(
            mergeMap(response =>
                of({
                    data: response.data,
                    total: response.total
                })
            ),
            tap(() => this.loading = false)
        );
    }

    protected post(queryStr: string, httpPostBody: any): Observable<GridDataResultCustom>
    {
        this.loading = true;
        return this.http.post(queryStr, httpPostBody, {
            observe: 'response',
            responseType: 'blob'
        }).pipe(
            mergeMap((response: HttpResponse<Blob>) =>
            {
                const contentType = response.headers.get('Content-Type') || '';
                return new Promise<GridDataResultCustom>((resolve, reject) =>
                {
                    if (contentType.includes('application/json'))
                    {
                        // Convert blob to text then parse JSON
                        response.body!.text()
                            .then((jsonText) =>
                            {
                                const parsed: GridDataResultCustom = JSON.parse(jsonText);
                                resolve({
                                    export: parsed.export || null,
                                    data: parsed.data,
                                    total: parsed.total
                                });
                            })
                            .catch((err) => reject(err));
                    }
                    else
                    {
                        // Handle file/blob response
                        const totalCountHeader = response.headers.get('X-Total-Count');
                        const totalCount = totalCountHeader ? parseInt(totalCountHeader, 10) : 0;
                        const blob = new Blob([response.body!], {
                            type: contentType || 'application/octet-stream'
                        });
                        resolve({
                            export: blob,
                            data: [],
                            total: totalCount
                        });
                    }
                });
            }),
            tap(() => this.loading = false)
        );
    }
}